/**
 * Every value here is a constant in the deployed contracts. Nothing in this file
 * is configuration: if one of these disagrees with the chain, the contracts are
 * right and this file is a bug. `assertConstants` in the indexer reads them back
 * off the chain at startup and refuses to run on a mismatch.
 */
// ---------------------------------------------------------------- Factory
/** 1,000,000,000 tokens, 18 decimals. Minted once, never again. */
export const SUPPLY = 1000000000n * 10n ** 18n;
/** The curve's phantom ETH reserve, which sets the opening price. */
export const PHANTOM_QUOTE = 10n ** 18n;
/** ETH the curve holds when it graduates. */
export const GRADUATION_THRESHOLD = 4n * 10n ** 18n;
/** Paid to the treasury on every launch. */
export const CREATION_FEE = 5n * 10n ** 14n;
/** 1.3% pool fee in Uniswap's hundredths of a bip. */
export const POOL_FEE = 13_000;
export const TICK_SPACING = 200;
export const MAX_NAME_LENGTH = 64;
export const MAX_SYMBOL_LENGTH = 16;
export const MAX_LOGO_LENGTH = 512;
export const MAX_DESCRIPTION_LENGTH = 2048;
export const MAX_SOCIAL_LENGTH = 256;
// ---------------------------------------------------------------- Curve
export const BASIS_POINTS = 10000n;
/** 1.3% of the ETH leg, on every trade, on the curve and in the pool alike. */
export const FEE_BPS = 130n;
/** 99% of a non-exempt buy's post-fee amount during the launch window. */
export const SNIPE_TAX_BPS = 9900n;
/** Seconds from the launch block in which the snipe tax applies. */
export const SNIPE_WINDOW = 3;
// ---------------------------------------------------------------- Fee split
/** Of every 130 parts the router receives: 50 creator, 50 arena, 30 protocol. */
export const TOTAL_PARTS = 130n;
export const CREATOR_PARTS = 50n;
export const ARENA_PARTS = 50n;
export const PROTOCOL_PARTS = 30n;
// ---------------------------------------------------------------- Arena
/** Four hours, counted from the block of the first launch. */
export const PERIOD = 4 * 60 * 60;
/** Fees a token's trades must pay in a period to enter its draw: 0.05 ETH. */
export const THRESHOLD = 5n * 10n ** 16n;
/** Rounds after a period's close whose beacon decides it. 20 rounds is one minute. */
export const ROUND_MARGIN = 20n;
/** Below this the arena treats its balance as empty. */
export const MIN_PURCHASE = 10n ** 12n;
/** Seconds after a close from which a period the beacon has not settled may be settled from a block hash. */
export const FALLBACK_DELAY = 7 * 24 * 60 * 60;
/** Blocks ahead the fallback commits to, so the committer cannot know the hash. */
export const FALLBACK_LEAD = 10;
/** A purchase slice moves the winner's price by at most 10%: sqrt(1.1) as a ratio. */
export const SQRT_IMPACT_NUM = 1048809n;
export const SQRT_IMPACT_DEN = 1000000n;
// ---------------------------------------------------------------- drand quicknet
export const DRAND_GENESIS = 1692803367n;
export const DRAND_PERIOD = 3n;
export const DRAND_CHAIN_HASH = '52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971';
export const DRAND_API = 'https://api.drand.sh/v2/beacons/quicknet';
// ---------------------------------------------------------------- Derived
/** Fraction of supply sold on the curve before graduation: R / (E0 + R) = 4/5. */
export const SOLD_AT_GRADUATION = (SUPPLY * GRADUATION_THRESHOLD) / (PHANTOM_QUOTE + GRADUATION_THRESHOLD);
/** Tokens the curve reserves and never sells: 200,000,000. */
export const RESERVED_TOKENS = SUPPLY - SOLD_AT_GRADUATION;
/** Of the reserve, what enters the pool with the 4 ETH: 160,000,000. */
export const POOL_TOKENS = (RESERVED_TOKENS * GRADUATION_THRESHOLD) / (PHANTOM_QUOTE + GRADUATION_THRESHOLD);
/** The rest of the reserve, burned at graduation: 40,000,000. */
export const BURNED_AT_GRADUATION = RESERVED_TOKENS - POOL_TOKENS;
/** Opening price in wei per token: 1e-9 ETH. */
export const OPENING_PRICE_WEI = (PHANTOM_QUOTE * 10n ** 18n) / SUPPLY;
/** Closing price in wei per token: 2.5e-8 ETH. */
export const GRADUATION_PRICE_WEI = ((PHANTOM_QUOTE + GRADUATION_THRESHOLD) * 10n ** 18n) / RESERVED_TOKENS;
/** Volume at which a token's fees reach the draw threshold: about 3.85 ETH. */
export const THRESHOLD_VOLUME = (THRESHOLD * BASIS_POINTS) / FEE_BPS;
//# sourceMappingURL=constants.js.map